import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { 
  Search, BookOpen, Home as HomeIcon, GraduationCap, ShoppingBag, 
  Briefcase, Music, Handshake, Shirt, ArrowRight, Flame, Trophy, 
  Sparkles, TrendingUp, User 
} from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const [loadingData, setLoadingData] = useState(true);
  const [user, setUser] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [latestQuestions, setLatestQuestions] = useState([]);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);

        // --- 1. RECUPERO DOMANDE RECENTI ---
        const { data: questionsData, error: qError } = await supabase
          .from('exam_questions')
          .select('*')
          .eq('is_approved', true) 
          .order('created_at', { ascending: false })
          .limit(4);
        
        if (qError) console.error("Errore domande:", qError);
        if (questionsData) setLatestQuestions(questionsData);

        // --- 2. RECUPERO CLASSIFICA ---
        const { data: leaderboardData, error: lError } = await supabase
            .from('profiles') 
            .select('email, points') 
            .gt('points', 0) 
            .order('points', { ascending: false })
            .limit(5);
            
        if (lError) console.error("Errore classifica:", lError);
        if (leaderboardData) setLeaderboard(leaderboardData);

      } catch (err) { 
        console.error("Errore caricamento dati:", err); 
      } finally { 
        setLoadingData(false);
      }
    };
    
    init();
  }, []);

  // --- NUOVA SERVICE CARD "STYLE MERCH" ---
  const ServiceCard = ({ to, color, icon: Icon, title, subtitle, colSpan = "md:col-span-1" }) => {
    
    // Mappatura Gradienti Pieni e Vibranti
    const colorMap = {
      indigo: "from-indigo-600 to-violet-700 shadow-indigo-500/30",
      green: "from-emerald-500 to-teal-700 shadow-emerald-500/30",
      purple: "from-fuchsia-600 to-purple-800 shadow-purple-500/30",
      orange: "from-orange-500 to-red-600 shadow-orange-500/30",
      sky: "from-blue-500 to-cyan-600 shadow-blue-500/30",
      pink: "from-pink-500 to-rose-600 shadow-pink-500/30",
      rose: "from-rose-500 to-red-700 shadow-rose-500/30",
    };

    const gradientClass = colorMap[color];

    return (
      <Link to={to} className={`${colSpan} group relative bg-gradient-to-br ${gradientClass} p-8 rounded-[2.5rem] shadow-2xl hover:shadow-xl hover:-translate-y-2 transition-all duration-500 overflow-hidden flex flex-col justify-between min-h-[280px]`}>
        
        {/* TEXTURE NOISE (Effetto Grana) */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light pointer-events-none"></div>
        
        {/* ICONA GIGANTE SFONDO (Decorazione) */}
        <div className="absolute -right-10 -bottom-10 text-white opacity-10 transform rotate-12 group-hover:rotate-45 group-hover:scale-110 transition-transform duration-700 pointer-events-none">
            <Icon size={220} />
        </div>

        {/* CONTENUTO Z-INDEX ALTO */}
        <div className="relative z-10">
            {/* Box Icona Glassmorphism */}
            <div className="bg-white/20 backdrop-blur-md border border-white/20 p-4 rounded-2xl w-fit mb-6 shadow-inner group-hover:scale-110 transition-transform duration-300">
              <Icon size={32} className="text-white drop-shadow-md"/>
            </div>
            
            {/* Testi Bianchi */}
            <h3 className="text-3xl font-black text-white mb-2 leading-tight tracking-tight drop-shadow-sm">{title}</h3>
            <p className="text-white/80 font-medium text-sm leading-relaxed max-w-[80%]">{subtitle}</p>
        </div>

        {/* Freccia Action */}
        <div className="relative z-10 mt-6 flex justify-end">
             <div className="bg-white text-slate-900 p-3 rounded-full shadow-lg opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
               <ArrowRight size={20} strokeWidth={3} />
             </div>
        </div>
      </Link>
    );
  };

  return (
    // --- MODIFICA SFONDO QUI: Gradiente molto più forte e visibile ---
    <div className="relative bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 min-h-screen flex flex-col items-center px-4 pt-24 pb-20 overflow-hidden font-sans text-slate-800 w-full">
      <SEO title="TusciaHub | Home" description="Il portale degli studenti di Viterbo." url="/" />

      {/* Background Blobs ancora più intensi per contrastare il nuovo sfondo */}
      <div className="absolute top-[-20%] left-[-20%] w-[80%] max-w-[800px] aspect-square bg-indigo-500/40 rounded-full blur-[120px] mix-blend-multiply filter animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-[-20%] right-[-20%] w-[70%] max-w-[700px] aspect-square bg-fuchsia-500/40 rounded-full blur-[120px] mix-blend-multiply filter pointer-events-none"></div>
      <div className="absolute top-[20%] right-[10%] w-[40%] max-w-[400px] aspect-square bg-sky-500/30 rounded-full blur-[100px] mix-blend-multiply filter pointer-events-none"></div>


      <div className="relative z-10 max-w-7xl mx-auto w-full">
        
        {/* HERO SECTION */}
        <div className="text-center max-w-5xl mx-auto mb-24 animate-in fade-in slide-in-from-bottom-8 duration-700 px-4">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-sm border border-indigo-100 text-indigo-600 text-xs font-black tracking-widest uppercase shadow-xl shadow-indigo-100/50 mb-8 transform hover:scale-105 transition-transform cursor-default">
               <Sparkles size={14} className="text-yellow-500 fill-yellow-500" /> Studenti Universitari
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-slate-900 tracking-tighter leading-[1.05] mb-8">
              L'Università <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 drop-shadow-sm">Senza Filtri.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto font-medium leading-relaxed mb-12">
              Tutto quello che ti serve per vivere l'università al meglio, in un unico posto.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dipartimenti" className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold text-lg hover:bg-slate-800 transition transform hover:-translate-y-1 shadow-2xl shadow-slate-300/50 flex items-center justify-center gap-2 w-full sm:w-auto">
                    <Search size={20}/> Trova il tuo Corso
                </Link>
                {!user ? (
                    <Link to="/login" className="px-8 py-4 bg-white text-indigo-600 border-2 border-indigo-50 rounded-2xl font-bold text-lg hover:bg-indigo-50 hover:border-indigo-200 transition transform hover:-translate-y-1 shadow-xl shadow-indigo-100/50 flex items-center justify-center gap-2 w-full sm:w-auto">
                        Unisciti Ora
                    </Link>
                ) : (
                    <Link to="/profile" className="px-8 py-4 bg-white text-slate-700 border-2 border-slate-100 rounded-2xl font-bold text-lg hover:bg-slate-50 hover:border-slate-200 transition transform hover:-translate-y-1 shadow-xl shadow-slate-100/50 flex items-center justify-center gap-2 w-full sm:w-auto">
                        <User size={20}/> Vai al Profilo
                    </Link>
                )}
            </div>
        </div>

        {/* --- RIQUADRI CLASSIFICA E DOMANDE (Stile Glass Light) --- */}
        <div className="grid md:grid-cols-5 gap-6 mb-24 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
            
            {/* BOX 1: ULTIME DOMANDE */}
            <div className="md:col-span-3 bg-white/80 backdrop-blur-md p-8 rounded-[2.5rem] shadow-xl shadow-red-100/50 border border-red-50 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-red-50 rounded-full blur-3xl -mr-20 -mt-20 group-hover:bg-red-100 transition-colors duration-500"></div>
                
                <div className="flex items-center justify-between mb-8 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-red-100 text-red-600 rounded-2xl shadow-sm"><Flame size={24} /></div>
                        <div>
                            <h3 className="font-black text-xl text-slate-900 leading-none">Ultim'ora Esami</h3>
                            <p className="text-sm text-slate-400 font-bold mt-1">Appena pubblicate</p>
                        </div>
                    </div>
                    <Link to="/dipartimenti" className="text-xs font-bold text-red-600 bg-red-50 px-4 py-2 rounded-full hover:bg-red-100 transition shadow-sm border border-red-100">Vedi tutte</Link>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 relative z-10">
                    {loadingData ? (
                        <>
                           <div className="h-20 bg-slate-50 rounded-2xl animate-pulse"></div>
                           <div className="h-20 bg-slate-50 rounded-2xl animate-pulse"></div>
                        </>
                    ) : latestQuestions.length > 0 ? (
                        latestQuestions.map(q => (
                        <Link 
                            to={`/subject/deim/eco-az/${encodeURIComponent(q.subject_id)}`} 
                            key={q.id} 
                            className="bg-white/50 hover:bg-white border border-red-50 hover:border-red-200 hover:shadow-lg p-5 rounded-2xl transition flex flex-col gap-2 group/item cursor-pointer shadow-sm h-full justify-center backdrop-blur-sm"
                        >
                            <span className="text-[10px] font-black text-red-400 uppercase tracking-wider bg-red-50/50 px-2 py-1 rounded-md w-fit border border-red-50 group-hover/item:text-red-600 group-hover/item:bg-red-50 group-hover/item:border-red-100 transition-colors line-clamp-1">{q.subject_id}</span>
                            <p className="text-sm font-bold text-slate-700 line-clamp-2 leading-snug group-hover/item:text-slate-900">"{q.question_text}"</p>
                        </Link>
                    ))) : (
                        <div className="col-span-full text-center py-6 text-slate-400 text-sm font-medium">Nessuna domanda recente approvata.</div>
                    )}
                </div>
            </div>

            {/* BOX 2: CLASSIFICA (Top 5) */}
            <div className="md:col-span-2 bg-white/80 backdrop-blur-md p-8 rounded-[2.5rem] shadow-xl shadow-yellow-100/50 border border-yellow-50 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-50 rounded-full blur-3xl -mr-20 -mt-20 group-hover:bg-yellow-100 transition-colors duration-500"></div>
                <div className="flex items-center gap-4 mb-8 relative z-10">
                    <div className="p-3 bg-yellow-100 text-yellow-600 rounded-2xl shadow-sm"><Trophy size={24} /></div>
                    <div>
                        <h3 className="font-black text-xl text-slate-900 leading-none">Top Studenti</h3>
                        <p className="text-sm text-slate-400 font-bold mt-1">Hall of Fame</p>
                    </div>
                </div>
                
                <div className="space-y-3 max-h-[240px] overflow-y-auto pr-2 custom-scrollbar relative z-10">
                    {loadingData ? (
                        <>
                            <div className="h-10 bg-slate-50 rounded-xl animate-pulse"></div>
                            <div className="h-10 bg-slate-50 rounded-xl animate-pulse"></div>
                            <div className="h-10 bg-slate-50 rounded-xl animate-pulse"></div>
                        </>
                    ) : leaderboard.length > 0 ? (
                        leaderboard.map((user, index) => (
                        <div key={index} className="flex items-center justify-between p-3 rounded-2xl hover:bg-yellow-50/50 transition border border-transparent hover:border-yellow-100 group/row bg-white/40 backdrop-blur-sm">
                            <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shadow-sm ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-yellow-200' : index === 1 ? 'bg-slate-300 text-white' : index === 2 ? 'bg-orange-300 text-white' : 'bg-slate-100 text-slate-400'}`}>{index + 1}</div>
                                <span className="font-bold text-sm text-slate-600 group-hover/row:text-slate-900 truncate max-w-[120px]">
                                    {user.email ? user.email.split('@')[0] : "Studente"}
                                </span>
                            </div>
                            <div className="font-black text-xs text-yellow-700 bg-yellow-50 px-3 py-1.5 rounded-lg border border-yellow-100">{user.points || 0} pt</div>
                        </div>
                    ))) : (
                        <p className="text-slate-400 text-sm text-center py-6">Classifica vuota.</p>
                    )}
                </div>
            </div>
        </div>

        {/* --- GRIGLIA SERVIZI VIBRANTE (BENTO GRID STYLE) --- */}
        <div className="mb-24">
            <h2 className="text-4xl font-black text-slate-900 mb-12 text-center flex items-center justify-center gap-3">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-slate-600">Esplora i Servizi</span>
            </h2>
            
            <div className="grid md:grid-cols-4 gap-6">
                {/* Queste card ora usano i gradienti forti e testo bianco */}
                <ServiceCard to="/dipartimenti" color="indigo" icon={BookOpen} title="Materiale Didattico" subtitle="Dispense, appunti e slide." colSpan="md:col-span-2" />
                <ServiceCard to="/affitti" color="green" icon={HomeIcon} title="Cerca Casa" subtitle="Stanze e appartamenti." />
                <ServiceCard to="/ripetizioni" color="purple" icon={GraduationCap} title="Ripetizioni" subtitle="Tutor per esami." />
                <ServiceCard to="/mercatino" color="orange" icon={ShoppingBag} title="Mercatino" subtitle="Compra e vendi libri usati." />
                <ServiceCard to="/jobs" color="sky" icon={Briefcase} title="Lavoro & Stage" subtitle="Offerte part-time." />
                <ServiceCard to="/eventi" color="pink" icon={Music} title="Eventi" subtitle="Nightlife e cultura." />
                <ServiceCard to="/partner" color="rose" icon={Handshake} title="Partner" subtitle="Sconti in città." />
            </div>
        </div>

        {/* --- MERCH BANNER --- */}
        <Link to="/merch" className="group relative bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 p-8 md:p-14 rounded-[3rem] shadow-2xl shadow-slate-400/50 overflow-hidden flex flex-col md:flex-row items-center justify-between mb-24 gap-10 text-center md:text-left border border-slate-800/50">
           {/* Texture */}
           <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay pointer-events-none"></div>
           {/* Decorazione */}
           <div className="absolute -left-24 -top-24 text-white/5 transform rotate-12 group-hover:rotate-45 transition-transform duration-1000"><Shirt size={400} /></div>
           
           <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
               <div className="bg-white/10 backdrop-blur-xl p-8 rounded-[2rem] text-white shadow-2xl border border-white/10 group-hover:scale-105 transition-transform duration-500">
                  <Shirt size={48} className="drop-shadow-lg"/>
               </div>
               <div className="text-white">
                  <h3 className="text-3xl md:text-4xl font-black mb-3 flex flex-col md:flex-row items-center gap-4">
                    TusciaHub Merch 
                    <span className="text-xs bg-indigo-500 text-white px-3 py-1.5 rounded-lg font-bold uppercase tracking-wider shadow-lg shadow-indigo-900/50">Coming Soon</span>
                  </h3>
                  <p className="text-indigo-200/80 font-medium text-lg md:text-xl max-w-lg leading-relaxed">
                    Felpe, t-shirt e accessori ufficiali per veri studenti. Presto disponibili.
                  </p>
               </div>
           </div>
           
           <div className="bg-white text-indigo-950 p-6 rounded-full shadow-lg shadow-indigo-900/50 relative z-10 group-hover:scale-110 group-hover:shadow-white/20 transition-all duration-300">
             <ArrowRight size={32} />
           </div>
        </Link>

      </div>
      <style jsx>{` .custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; } `}</style>
    </div>
  );
};

export default Home;